import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// A custom dropdown for the buyer checkout. A native <select> renders its option
// list with OS-native chrome that CSS cannot style (the dark macOS list), so
// every select on the live page looked unbranded. This replaces it with a
// styled, keyboard-accessible listbox that matches the reference design
// (RegionalPlansPage): rich rows (flag + name + dial code) and an optional
// search box for long lists like the country picker.
//
// Buyer-only interactive chrome — self-contained with inline styles.
// The classic template now uses MUI (makeStyles); checkout-classic.css has been removed.
//
// Each option: { value, label, trigger?, node?, search? }
//   label   — text shown when nothing richer is provided (form-field selects)
//   trigger — what the closed button shows (defaults to label)
//   node    — JSX for the menu row (defaults to label)
//   search  — text matched against the query (defaults to label)
const CARET = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flex: "none" }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SEARCH_ICON = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#9aa19d"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flex: "none" }}
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export default function CkSelect({
  value,
  onChange,
  options,
  placeholder = "Select",
  variant = "field", // "field" = full-width form field; "compact" = phone prefix
  bare = false, // no own border/bg — for use inside a field adornment
  searchable = false,
  searchPlaceholder = "Search…",
  ariaLabel,
  id,
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const openRef = useRef(false);

  // In a ref so an inline arrow from the parent doesn't churn `close`, which
  // the outside-click effect depends on.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const compact = variant === "compact";
  const selected = options.find((o) => o.value === value) || null;

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      String(o.search != null ? o.search : o.label || "")
        .toLowerCase()
        .includes(q)
    );
  }, [options, searchable, query]);

  const close = useCallback((refocus) => {
    setOpen(false);
    setHighlight(-1);
    setQuery("");
    if (openRef.current) {
      openRef.current = false;
      if (onOpenChangeRef.current) onOpenChangeRef.current(false);
    }
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  const openMenu = useCallback(() => {
    setOpen(true);
    const idx = options.findIndex((o) => o.value === value);
    setHighlight(idx >= 0 ? idx : 0);
    if (!openRef.current) {
      openRef.current = true;
      if (onOpenChangeRef.current) onOpenChangeRef.current(true);
    }
  }, [options, value]);

  const choose = (opt) => {
    if (!opt) return;
    onChange(opt.value);
    close(true);
  };

  // Close when clicking outside the component. Also on pointerdown, because on
  // touch the compat mouse events trail the whole pointer sequence — a
  // mousedown-only close lands after ClassicTemplate's pointerup sync and
  // leaves its bar suppression stale. close() is idempotent via openRef.
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("mousedown", onDocPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("mousedown", onDocPointerDown);
    };
  }, [open, close]);

  // Focus the search box when a searchable menu opens.
  useEffect(() => {
    if (open && searchable && searchRef.current) searchRef.current.focus();
  }, [open, searchable]);

  // Unmounting while open must still report closed, or the parent stays
  // suppressed. openRef keeps never-opened selects from emitting on unmount.
  useEffect(
    () => () => {
      if (openRef.current && onOpenChangeRef.current) {
        onOpenChangeRef.current(false);
      }
    },
    []
  );

  // Filtering can shrink the list past the current highlight; keep it in range.
  useEffect(() => {
    if (!open) return;
    setHighlight((h) => (filtered.length ? Math.min(h < 0 ? 0 : h, filtered.length - 1) : -1));
  }, [open, filtered.length]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.children[highlight];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const onKeyDown = (e) => {
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(filtered[highlight]);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const menuWidth = compact
    ? searchable
      ? { minWidth: 268, maxWidth: 320 }
      : { minWidth: 160 }
    : { right: 0 };

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        ...(compact ? {} : { width: "100%" }),
        ...(bare ? { height: "100%" } : {}),
      }}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onKeyDown}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: compact ? 4 : 8,
          width: compact ? undefined : "100%",
          boxSizing: "border-box",
          fontSize: compact ? 14 : 15,
          color: selected ? "#333" : "#9aa19d",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          lineHeight: "normal",
          // A bare trigger has no box of its own — the surrounding adornment
          // (countryCodeInput) supplies the tint, divider and rounding.
          ...(bare
            ? {
                height: "100%",
                minHeight: 0,
                padding: "0 6px",
                background: "transparent",
                border: "none",
                borderRadius: 0,
              }
            : {
                minHeight: 40,
                padding: compact ? "0 10px" : "0 12px",
                background: compact ? "#f9f9f9" : "#fff",
                // Match the reference form-field border (0.5px #e0e0e0), not
                // MUI's default darker/thicker outline.
                border: `0.5px solid ${open ? "var(--accent)" : "#e0e0e0"}`,
                borderRadius: 4,
              }),
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.trigger || selected.label : placeholder}
        </span>
        <span
          style={{
            color: compact ? "#5b6660" : "#9aa19d",
            display: "flex",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 120ms ease",
          }}
        >
          {CARET}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            ...menuWidth,
            display: "flex",
            flexDirection: "column",
            maxHeight: 320,
            background: "#fff",
            border: "1px solid #eef1f0",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(16,24,40,0.15)",
            overflow: "hidden",
            zIndex: 30,
          }}
        >
          {searchable && (
            <div
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                padding: "8px 12px 4px",
                flex: "none",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingBottom: 5,
                  borderBottom: `1px solid ${searchFocused ? "var(--accent)" : "#e0e0e0"}`,
                  transition: "border-color 120ms ease",
                }}
              >
                {SEARCH_ICON}
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontFamily: "inherit",
                    fontSize: 14,
                    color: "#333",
                    width: "100%",
                  }}
                />
              </div>
            </div>
          )}

          <ul
            ref={listRef}
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              overflowY: "auto",
              flex: 1,
            }}
          >
            {filtered.map((o, i) => {
              const isSelected = o.value === value;
              const isHi = i === highlight;
              // Match the reference (MUI Select): full-width rows, plain text, a
              // neutral grey highlight for hover/keyboard and a slightly stronger
              // grey for the current selection — no accent tint, no check mark.
              let background = "transparent";
              if (isHi && isSelected) background = "rgba(0,0,0,0.10)";
              else if (isSelected) background = "rgba(0,0,0,0.06)";
              else if (isHi) background = "rgba(0,0,0,0.04)";
              return (
                <li
                  key={o.value != null ? o.value : i}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 40,
                    padding: "0 16px",
                    fontSize: compact ? 14 : 15,
                    cursor: "pointer",
                    color: "#333",
                    background,
                  }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.node || o.label}
                  </span>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 40,
                  padding: "0 16px",
                  fontSize: 14,
                  color: "#9aa19d",
                }}
              >
                No countries found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
